import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Phone, 
  Clock, 
  MapPin, 
  Award, 
  Wrench, 
  Droplet, 
  Star, 
  ExternalLink,
  CheckCircle,
  Shield,
  Users,
  Zap,
  MessageCircle,
  Calendar
} from 'lucide-react';

const Reviews = () => {
  // SEO meta tags setup
  useEffect(() => {
    
    const metaTags = [
      { name: 'description', content: 'Read 1000+ verified customer reviews for Colorado First Plumbing. 4.8★ rating across Google, BirdEye, HomeAdvisor, and Angi. See why Colorado customers trust us for emergency plumbing and sewer repair.' },
      { name: 'keywords', content: 'Colorado First Plumbing reviews, plumber reviews Denver, Google reviews plumber Colorado, customer testimonials plumbing, 4.8 star plumber, verified plumbing reviews' },
      { property: 'og:title', content: 'Customer Reviews | Colorado First Plumbing | 4.8★ Rating (1000+ Reviews Across All Platforms)' },
      { property: 'og:description', content: 'Read 1000+ verified customer reviews for Colorado First Plumbing. 4.8★ rating across Google, BirdEye, HomeAdvisor, and Angi.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/reviews' },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });
    
    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/reviews');
      document.head.appendChild(canonicalLink);
    } else {
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/reviews');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section - PSYCHOLOGY-OPTIMIZED with CRITICAL pt-48 spacing */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10 py-16 md:py-24">
            <div className="inline-block px-4 py-2 mb-6 rounded-full bg-trust-light text-trust-deep font-semibold text-sm border border-trust-primary/20">
              ⭐ Verified Customer Reviews
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-neutral-white">
              1000+ Customer Reviews Across All Platforms
            </h1>
            <p className="text-xl mb-4 text-trust-light">
              4.8★ Rating • Google • BirdEye • HomeAdvisor • Angi • BBB A+ Rating
            </p>
            <p className="text-lg mb-8 text-trust-light/90">
              See why Colorado customers trust Colorado First Plumbing for emergency plumbing, sewer repair, and residential services
            </p>
            
            {/* Trust Signals - Enhanced for Reviews Page */}
            <div className="flex flex-wrap justify-center items-center gap-8 text-trust-light mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center shadow-lg">
                  <Star size={24} className="text-white fill-current" />
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-neutral-white">4.8★</div>
                  <div className="text-sm text-trust-light">Overall Rating</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center shadow-lg">
                  <Users size={24} className="text-white" />
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-neutral-white">360+</div>
                  <div className="text-sm text-trust-light">Google Reviews</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle size={24} className="text-white" />
                </div>
                <div className="text-left">
                  <div className="text-2xl font-bold text-neutral-white">6+</div>
                  <div className="text-sm text-trust-light">Platforms</div>
                </div>
              </div>
            </div>
            
            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See Transparent Pricing
              </a>
            </div>
          </div>
        </section>

        {/* Review Platforms Section - TRUST PSYCHOLOGY */}
        <section className="py-16 md:py-20 bg-neutral-light">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-authority-navy">
                Verified Reviews Across Multiple Platforms
              </h2>
              <p className="text-lg text-neutral-gray">
                We don't hide our reviews. See authentic customer feedback across every major review platform
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {/* Google Reviews Card - PRIORITY PLATFORM */}
              <a 
                href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-neutral-white rounded-xl p-8 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-trust-primary/5 to-success-primary/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <span className="text-white font-bold text-2xl">G</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-authority-navy">Google Reviews</h3>
                  <div className="flex justify-center space-x-1 text-success-primary mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={24} className="fill-current" />
                    ))}
                  </div>
                  <div className="text-3xl font-bold text-authority-navy mb-2">4.8★</div>
                  <div className="text-neutral-gray font-medium mb-4">(360+ reviews)</div>
                  <div className="flex items-center justify-center text-trust-primary font-semibold">
                    <span>View Reviews</span>
                    <ExternalLink size={16} className="ml-2" />
                  </div>
                </div>
              </a>

              {/* BirdEye Reviews Card */}
              <a 
                href="https://birdeye.com/colorado-first-plumbing-and-sewer-172384402926614" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-neutral-white rounded-xl p-8 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-trust-primary/5 to-action-primary/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <span className="text-white font-bold text-2xl">B</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-authority-navy">BirdEye Reviews</h3>
                  <div className="flex justify-center space-x-1 text-success-primary mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={24} className="fill-current" />
                    ))}
                  </div>
                  <div className="text-3xl font-bold text-authority-navy mb-2">5.0★</div>
                  <div className="text-neutral-gray font-medium mb-4">(300+ reviews)</div>
                  <div className="flex items-center justify-center text-trust-primary font-semibold">
                    <span>View Reviews</span>
                    <ExternalLink size={16} className="ml-2" />
                  </div>
                </div>
              </a>

              {/* HomeAdvisor Reviews Card */}
              <a 
                href="https://www.homeadvisor.com/rated.CFPPlumbingLLC.63409840.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-neutral-white rounded-xl p-8 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-action-primary/5 to-action-urgent/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <span className="text-white font-bold text-2xl">H</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-authority-navy">HomeAdvisor</h3>
                  <div className="flex justify-center space-x-1 text-success-primary mb-3">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} size={24} className="fill-current" />
                    ))}
                    <div className="relative">
                      <Star size={24} className="text-neutral-gray" />
                      <div className="absolute top-0 left-0 overflow-hidden w-4/5">
                        <Star size={24} className="text-success-primary fill-current" />
                      </div>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-authority-navy mb-2">4.7★</div>
                  <div className="text-neutral-gray font-medium mb-4">(92 reviews)</div>
                  <div className="flex items-center justify-center text-trust-primary font-semibold">
                    <span>View Reviews</span>
                    <ExternalLink size={16} className="ml-2" />
                  </div>
                </div>
              </a>
            </div>

            {/* Additional Platforms Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Angi Reviews Card */}
              <a 
                href="https://www.angi.com/companylist/us/co/thornton/colorado-first-plumbing-and-sewer-llc-reviews-9684474.htm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl hover:border-success-primary transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative z-10 text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <span className="text-white font-bold text-lg">A</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-authority-navy">Angi Reviews</h3>
                  <div className="flex justify-center space-x-1 text-success-primary mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <div className="text-xl font-bold text-authority-navy">Excellent</div>
                  <div className="text-sm text-neutral-gray">Multiple Reviews</div>
                </div>
              </a>

              {/* BBB Reviews Card */}
              <a 
                href="https://www.bbb.org/us/co/highlands-ranch/profile/plumber/colorado-first-plumbing-and-sewer-llc-1296-90268915" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl hover:border-success-primary transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative z-10 text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <Shield size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-authority-navy">BBB Accredited</h3>
                  <div className="flex justify-center space-x-1 text-success-primary mb-2">
                    <span className="text-2xl font-bold text-success-primary">A+</span>
                  </div>
                  <div className="text-lg font-bold text-authority-navy">Rating</div>
                  <div className="text-sm text-neutral-gray">Better Business Bureau</div>
                </div>
              </a>

              {/* BuildZoom Card */}
              <a 
                href="https://www.buildzoom.com/contractor/colorado-first-plumbing-and-sewer-inc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl hover:border-success-primary transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="relative z-10 text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-action-warm to-action-primary rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <span className="text-white font-bold text-lg">B</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-authority-navy">BuildZoom</h3>
                  <div className="flex justify-center space-x-1 text-success-primary mb-2">
                    <span className="text-xl font-bold text-success-primary">116</span>
                  </div>
                  <div className="text-lg font-bold text-authority-navy">Score</div>
                  <div className="text-sm text-neutral-gray">Top 2% of Contractors</div>
                </div>
              </a>
            </div>
            
            {/* Additional Review Sources Note */}
            <div className="mt-8 text-center">
                          </div>
          </div>
        </section>

        {/* Featured Customer Reviews Section - SOCIAL PROOF */}
        <section className="py-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-white">
                What Our Customers Are Saying
              </h2>
              <p className="text-lg text-trust-light">
                Real reviews from real customers across Colorado Metro area
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Google Review 1 - Emergency Sewer Response */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-action-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-red-50 px-2 py-1 rounded text-red-600">Google</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "It was an amazing job by Codee and his team! We will definitely tell all of our friends and family about his prompt and professional service! We had a sewer back up and Codee was very quick to approach the issue within a couple hours!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    E
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">Emergency Customer</p>
                    <p className="text-sm text-neutral-gray">Denver Metro, CO</p>
                  </div>
                </div>
              </div>

              {/* HomeAdvisor Review - Professional Service */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-trust-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-orange-50 px-2 py-1 rounded text-orange-600">HomeAdvisor</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Colorado First Plumbing is first-class! They were on time, worked efficiently and were extremely courteous. They even fixed a minor gas leak for me for free that they smelled. Highly recommend!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    P
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">Professional Service Customer</p>
                    <p className="text-sm text-neutral-gray">Denver Metro, CO</p>
                  </div>
                </div>
              </div>

              {/* BirdEye Review - After Hours Service */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-success-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-blue-50 px-2 py-1 rounded text-blue-600">BirdEye</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Reece responded to our plumbing emergency after hours. He was friendly, professional and thorough. He successfully trouble shot the water dripping from our ceiling and didn't leave until he was certain the issue was fixed."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    R
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">After Hours Customer</p>
                    <p className="text-sm text-neutral-gray">Lakewood, CO</p>
                  </div>
                </div>
              </div>

              {/* Angi Review - Kitchen Sink Service */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-action-warm shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-green-50 px-2 py-1 rounded text-green-600">Angi</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Nick from Colorado First Plumbing did an amazing job on a stoppage in my kitchen sink! He was very professional and personable. He even gave me tips to keep the sink running smoothly and fixed a toilet issue for free!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-action-warm to-action-primary rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    N
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">Kitchen Sink Customer</p>
                    <p className="text-sm text-neutral-gray">Denver Metro, CO</p>
                  </div>
                </div>
              </div>

              {/* HomeAdvisor Review */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-trust-deep shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-orange-50 px-2 py-1 rounded text-orange-600">HomeAdvisor</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "I was impressed with Codee's knowledge and quick responses, plus he was a delight to work with. I would call him again in a heartbeat were I to need plumbing work in the future."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-trust-deep to-authority-navy rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    C
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">Codee Customer</p>
                    <p className="text-sm text-neutral-gray">Centennial, CO</p>
                  </div>
                </div>
              </div>

              {/* Angi Review */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-success-eco shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-green-50 px-2 py-1 rounded text-green-600">Angi</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Nick couldn't have been nicer. He determined quickly it was definitely my guest bath toilet. We had issues getting the toilet from Home Depot but they were terrific in communicating and finding it from another source."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-success-eco to-success-primary rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    H
                  </div>
                  <div className="ml-3">
                                        <p className="text-sm text-neutral-gray">Lakewood, CO</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges & Certifications Section - AUTHORITY PSYCHOLOGY */}
        <section className="py-16 bg-neutral-light">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-authority-navy">
                Licensed, Certified & Trusted
              </h2>
              <p className="text-lg text-neutral-gray">
                Our credentials and certifications demonstrate our commitment to excellence and professional standards
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Licensed Master Plumber Badge */}
              <div className="bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-authority-navy">Licensed Master Plumber</h3>
                <p className="text-sm text-neutral-gray">Colorado State Licensed & Certified Master Plumber</p>
              </div>

              {/* BBB A+ Rating Badge */}
              <div className="bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Shield size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-authority-navy">BBB A+ Rating</h3>
                <p className="text-sm text-neutral-gray">Better Business Bureau Accredited Business</p>
              </div>

              {/* Fully Insured Badge */}
              <div className="bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Shield size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-authority-navy">Fully Insured</h3>
                <p className="text-sm text-neutral-gray">Liability Insurance & Worker's Compensation</p>
              </div>

              {/* BuildZoom Top 2% Badge */}
              <div className="bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-action-warm to-action-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Award size={32} className="text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-authority-navy">Top 2% Contractor</h3>
                <p className="text-sm text-neutral-gray">BuildZoom Score 116 - Elite Contractor Status</p>
              </div>
            </div>

            {/* Expertise Award Section */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-trust-light to-success-light rounded-2xl p-8 border border-trust-primary/20">
                <h3 className="text-2xl font-bold mb-4 text-authority-navy">Award-Winning Service</h3>
                <p className="text-neutral-gray mb-6">
                  Recognized by Expertise.com as one of the top plumbing companies in Thornton, Colorado
                </p>
                
                {/* Expertise Award - Using the award code from Review-Sources.txt */}
                <div className="flex justify-center">
                  <a href="https://www.expertise.com/co/thornton/plumbing" target="_blank" rel="noopener noreferrer" className="inline-block hover:scale-105 transition-transform duration-300">
                    <img 
                      style={{ width: '200px', display: 'block' }} 
                      width="200" 
                      height="160" 
                      src="https://res.cloudinary.com/expertise-com/image/upload/remote_media/awards/co_thornton_plumbing_2025_transparent.svg" 
                      alt="Top Plumbing in Thornton" 
                      className="shadow-lg rounded-lg"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section - COMPETITIVE PSYCHOLOGY */}
        <section className="py-16 bg-neutral-white">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-authority-navy">
                Why Colorado Customers Choose Us
              </h2>
              <p className="text-xl text-neutral-gray">
                See what makes Colorado First Plumbing different from other plumbing companies
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
              {/* What Others Do - Warning Psychology */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-red-800 flex items-center">
                  <span className="text-3xl mr-3">❌</span> What Other Plumbers Do:
                </h3>
                <div className="space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-1">"We need to send someone out to take a look"</p>
                    <p className="text-red-600 text-sm">Translation: We're sending a salesman to pressure you</p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-1">"$100 OFF Today Only!"</p>
                    <p className="text-red-600 text-sm">$100 off what? They won't tell you the actual price</p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-1">Hidden reviews or fake testimonials</p>
                    <p className="text-red-600 text-sm">They hide their real reviews or post fake ones</p>
                  </div>
                </div>
              </div>

              {/* What We Do - Success Psychology */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-success-primary flex items-center">
                  <span className="text-3xl mr-3">✅</span> What We Do Different:
                </h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-success-primary to-success-eco p-4 rounded-lg text-white border-l-4 border-success-eco shadow-lg">
                    <p className="font-semibold text-white mb-1">1000+ verified reviews across 10+ platforms</p>
                    <p className="text-success-light text-sm">Real customers, real reviews, nothing hidden</p>
                  </div>
                  <div className="bg-gradient-to-r from-success-primary to-success-eco p-4 rounded-lg text-white border-l-4 border-success-eco shadow-lg">
                    <p className="font-semibold text-white mb-1">Actual prices online right now</p>
                    <p className="text-success-light text-sm">See water heater, toilet, disposal costs before we arrive</p>
                  </div>
                  <div className="bg-gradient-to-r from-success-primary to-success-eco p-4 rounded-lg text-white border-l-4 border-success-eco shadow-lg">
                    <p className="font-semibold text-white mb-1">No salesmen - just skilled plumbers</p>
                    <p className="text-success-light text-sm">Our techs fix problems, they don't sell you things you don't need</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Review Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-trust-primary mb-2">360+</div>
                <div className="text-neutral-gray">Google Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-action-primary mb-2">4.8★</div>
                <div className="text-neutral-gray">Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-success-primary mb-2">10+</div>
                <div className="text-neutral-gray">Review Platforms</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-trust-deep mb-2">2018</div>
                <div className="text-neutral-gray">Serving Colorado</div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section - CONVERSION PSYCHOLOGY */}
        <section className="py-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-white">
              Ready to Experience 5-Star Service?
            </h2>
            <p className="text-xl text-trust-light mb-8">
              Join hundreds of satisfied Colorado customers who trust Colorado First Plumbing
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See Transparent Pricing
              </a>
            </div>
            
            <p className="text-trust-light text-sm">
              Licensed Master Plumber • BBB A+ Rating • Serving Colorado Since 2018 • 24/7 Emergency Service
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Reviews;
